import { Message, useMessage, useMessageGetter } from '@messageformat/react';
import { Col, List, Row, Tab, Tabs, Tile, Expander, useLocale } from '@op/opds.opux';
import { IconFunds } from '@op/opds.opux-icons/finance';
import { IconInvestments } from '@op/opds.opux-icons/business';
import { IconFingerprint, IconSolution } from '@op/opds.opux-icons/misc';
import { IconLinkInternal } from '@op/opds.opux-icons/navigation';
import { space } from '@op/opds.tokens';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import LandingPageListItem from './LandingPageListItem';
import { ReactComponent as MainImage } from '../images/main.svg';
import { ReactComponent as RocketImage } from '../images/rocket.svg';
import { ReactComponent as MountainImage } from '../images/mountain.svg';
import { useIsMobile } from '../util/mediaQuery';
import IllustrationWithProgress from './ProgressIndicator/IllustrationWithProgress';
import {
  analyticsActionGroup,
  analyticsActionName,
  analyticsPhaseName,
  sendAnalyticsAction,
  sendAnalyticsPhase,
  analyticsPhaseCategory,
} from '../util/analytics';
import LinkButton from './LinkButton';
import routes from '../messages/routes';
import styles from './LandingPage.module.scss';

interface LandingPageProps {
  showTab: number;
}

const LandingPage = ({ showTab }: LandingPageProps): JSX.Element => {
  const getLandingTexts = useMessageGetter('');
  const readMore = useMessage('read_more_body');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const locale = useLocale();
  const { courses } = routes[locale];

  useEffect(() => {
    if (showTab === 0) {
      sendAnalyticsPhase(analyticsPhaseCategory.START, analyticsPhaseName.INTRO);
    } else if (showTab === 1) {
      sendAnalyticsPhase(analyticsPhaseCategory.START, analyticsPhaseName.COURSES);
    }
  }, [showTab]);

  return (
    <div style={{ paddingTop: space.default }}>
      <h1 className="opds-styles-v1-text-hero-medium" style={{ textAlign: 'center' }}>
        <Message id="investor_academy_title" />
      </h1>
      <Tabs
        style={{ justifyContent: 'center' }}
        active={showTab}
        onChange={(_, index) => {
          navigate(index === 0 ? '/' : courses);
        }}
      >
        <Tab
          style={{ display: 'flex', flexDirection: 'column' }}
          label={getLandingTexts('introduction')}
        >
          <Row top="xs" middle="md" center="xs" reverse>
            <Col md={6} xs={12}>
              <MainImage className={styles.mainImage} aria-hidden="true" />
            </Col>
            <Col md={6} xs={12}>
              <h2 className={styles.title}>
                <Message id="learn_basics" />
              </h2>
              <p className={styles.listDescription}>
                <Message id="you_will_learn" />
              </p>
              <Tile wrapContent={false}>
                <List
                  border="inside"
                  indented
                  style={{ margin: 0, textAlign: isMobile ? 'center' : undefined }}
                >
                  <LandingPageListItem
                    msg="what_investing_means"
                    iconContent={<IconFunds size="large" />}
                  />
                  <LandingPageListItem
                    msg="how_to_make_first"
                    iconContent={<IconSolution size="large" />}
                  />
                  <LandingPageListItem
                    msg="suitable_investing"
                    iconContent={<IconFingerprint size="large" />}
                  />
                  <LandingPageListItem
                    msg="creating_portfolio"
                    iconContent={<IconInvestments size="large" />}
                  />
                </List>
              </Tile>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <LinkButton
                  className={styles.startButton}
                  icon={IconLinkInternal}
                  iconPos="right"
                  size="large"
                  primary
                  to={`${courses}/1`}
                  onClick={() => {
                    sendAnalyticsAction(
                      analyticsActionName.START,
                      analyticsActionGroup.CLICK,
                      analyticsPhaseName.INTRO,
                    );
                  }}
                >
                  <Message id="start" />
                </LinkButton>
              </div>
              <Expander
                headerTag="h3"
                style={{ marginTop: space.default, marginBottom: space.xl }}
                title={getLandingTexts('read_more')}
                onClick={() => {
                  return sendAnalyticsAction(
                    analyticsActionName.READ_MORE,
                    analyticsActionGroup.EXPANDER,
                    analyticsPhaseName.INTRO,
                  );
                }}
              >
                <ul style={{ margin: 0 }} className="opux-v6__opds">
                  {Object.keys(readMore).map((item: string, index: number) => {
                    return (
                      <li key={item}>
                        <Message id={`read_more_body.${index}`} />
                      </li>
                    );
                  })}
                </ul>
              </Expander>
            </Col>
          </Row>
        </Tab>
        <Tab
          style={{ display: 'flex', flexDirection: 'column' }}
          label={getLandingTexts('courses')}
        >
          <Row center="xs">
            <Col xs={12} md={6}>
              <IllustrationWithProgress
                max={5}
                value={0}
                svg={<RocketImage aria-hidden="true" />}
              />
              <h2 className={styles.subtitle}>
                <Message id="how_to_start" />
              </h2>
              <Tile wrapContent={false} style={{ marginBottom: space.large }}>
                <List border="inside" indented style={{ margin: 0 }}>
                  <LandingPageListItem msg="course1.title" course={1} iconContent={1} />
                  <LandingPageListItem msg="course2.title" course={2} iconContent={2} />
                  <LandingPageListItem msg="course3.title" course={3} iconContent={3} />
                  <LandingPageListItem msg="course4.title" course={4} iconContent={4} />
                  <LandingPageListItem msg="course5.title" course={5} iconContent={5} />
                </List>
              </Tile>
            </Col>
            <Col xs={12} md={6}>
              <IllustrationWithProgress
                max={5}
                value={0}
                svg={<MountainImage aria-hidden="true" />}
              />
              <h2 className={styles.subtitle}>
                <Message id="learn_more" />
              </h2>
              <Tile wrapContent={false}>
                <List border="inside" indented style={{ margin: 0 }}>
                  <LandingPageListItem msg="course6.title" course={6} iconContent={6} />
                  <LandingPageListItem msg="course7.title" course={7} iconContent={7} />
                  <LandingPageListItem msg="course8.title" course={8} iconContent={8} />
                  <LandingPageListItem msg="course9.title" course={9} iconContent={9} />
                  <LandingPageListItem
                    msg="course10.title"
                    course={10}
                    iconContent={10}
                  />
                </List>
              </Tile>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </div>
  );
};

export default LandingPage;
